import type { Locator, Page } from '@playwright/test';
import { decodePng, encodePng, cropPixels } from './png-codec.js';
// tesseract.js is an optional peer, imported when text is first read (ocr-tesseract.ts), never at module load.
import { loadTesseract, type RecognizeResult, type TesseractSegment, type WorkerParams } from './ocr-tesseract.js';
import * as fs from 'fs';
import { promisify } from 'util';
import type { Rect } from './types.js';

const readFile = promisify(fs.readFile);

type TextQuery = string | RegExp;

type MatchMode = 'word' | 'line' | 'paragraph';
type MatchStrategy = 'exact' | 'substring' | 'regex';

export interface OcrOptions {
  /** Language(s) to pass to Tesseract. Defaults to `eng`. */
  language?: string;
  /** Confidence threshold (0-100). Defaults to 60. */
  confidenceThreshold?: number;
  /** Optional crop rectangle to limit the screenshot area. */
  cropRect?: Rect;
  /**
   * Tesseract ENGINE parameters (e.g. `tessedit_char_whitelist`, `tessedit_pageseg_mode`).
   * Applied through `createWorker`'s config slot — see `recognizeWithParams`
   * for why passing these to `Tesseract.recognize` silently does nothing.
   */
  tesseractOptions?: Partial<WorkerParams>;
}

export interface OcrSearchOptions extends OcrOptions {
  /** Whether to match at word, line, or paragraph granularity. Defaults to `word`. */
  matchMode?: MatchMode;
  /** Strategy for matching string queries. Defaults to `substring`. */
  matchStrategy?: MatchStrategy;
  /** Treat string comparisons as case-sensitive. Defaults to false. */
  caseSensitive?: boolean;
  /** Maximum number of matches to return. Unlimited when omitted. */
  maxResults?: number;
}

export interface OcrMatch {
  /** Text content for the matched segment. */
  text: string;
  /** Confidence score (0-100) reported by Tesseract. */
  confidence: number;
  /** Bounding box in screenshot coordinates. */
  bounds: Rect;
  /** Level of the text node that produced the match. */
  level: MatchMode;
}

export interface RecognizedText {
  /** Raw Tesseract recognition result. */
  raw: RecognizeResult;
  /** Screenshot metadata useful for downstream consumers. */
  screenshot: {
    width: number;
    height: number;
    offsetX: number;
    offsetY: number;
  };
}

interface ScreenshotResult {
  buffer: Buffer;
  width: number;
  height: number;
  offsetX: number;
  offsetY: number;
}

async function readImageBuffer(image: string | Buffer): Promise<Buffer> {
  if (Buffer.isBuffer(image)) {
    return image;
  }
  return readFile(image);
}

/**
 * A PNG's size, read from its IHDR chunk — the first chunk, right after the 8-byte signature — without decoding a
 * pixel. Anything that is not a PNG goes to the decoder, which says why it cannot read it.
 */
function pngSize(buffer: Buffer): { width: number; height: number } {
  const isPng = buffer.length >= 24
    && buffer.readUInt32BE(0) === 0x89504e47 && buffer.readUInt32BE(4) === 0x0d0a1a0a
    && buffer.toString('latin1', 12, 16) === 'IHDR';
  if (!isPng) {
    const { width, height } = decodePng(buffer);
    return { width, height };
  }
  return { width: buffer.readUInt32BE(16), height: buffer.readUInt32BE(20) };
}

async function captureScreenshot(source: Page | Locator, cropRect?: Rect): Promise<ScreenshotResult> {
  const screenshotBuffer = await source.screenshot();

  if (!cropRect) {
    // Uncropped, the screenshot goes to recognition as it is: only its size is needed here.
    return {
      buffer: screenshotBuffer,
      ...pngSize(screenshotBuffer),
      offsetX: 0,
      offsetY: 0,
    };
  }

  const pixels = decodePng(screenshotBuffer);

  const { x, y, width, height } = cropRect;

  if (x < 0 || y < 0 || width <= 0 || height <= 0) {
    throw new Error(`OCR crop rectangle must have positive size and non-negative origin. Received ${JSON.stringify(cropRect)}.`);
  }
  if (x + width > pixels.width || y + height > pixels.height) {
    throw new Error(`OCR crop rectangle ${JSON.stringify(cropRect)} is out of bounds for screenshot ${pixels.width}x${pixels.height}.`);
  }

  const cropped = cropPixels(pixels, x, y, width, height);

  return {
    buffer: Buffer.from(encodePng(cropped)),
    width,
    height,
    offsetX: x,
    offsetY: y,
  };
}

function mapBoundingBox(bbox: { x0: number; y0: number; x1: number; y1: number }, offsetX: number, offsetY: number): Rect {
  const width = bbox.x1 - bbox.x0;
  const height = bbox.y1 - bbox.y0;
  return {
    x: bbox.x0 + offsetX,
    y: bbox.y0 + offsetY,
    width,
    height,
  };
}

function normalizeText(value: string, caseSensitive: boolean): string {
  return caseSensitive ? value.trim() : value.trim().toLowerCase();
}

function matchesQuery(text: string, query: TextQuery, strategy: MatchStrategy, caseSensitive: boolean): boolean {
  if (typeof query === 'string') {
    const haystack = normalizeText(text, caseSensitive);
    const needle = normalizeText(query, caseSensitive);

    switch (strategy) {
      case 'exact':
        return haystack === needle;
      case 'regex': {
        const flags = caseSensitive ? '' : 'i';
        const regex = new RegExp(needle, flags);
        return regex.test(text);
      }
      case 'substring':
      default:
        return haystack.includes(needle);
    }
  }

  if (query instanceof RegExp) {
    return query.test(text);
  }

  return false;
}

function getSegments(result: RecognizeResult, mode: MatchMode) {
  switch (mode) {
    case 'line':
      return result.data.lines ?? [];
    case 'paragraph':
      return result.data.paragraphs ?? [];
    case 'word':
    default:
      return result.data.words ?? [];
  }
}

/**
 * Recognize `buffer`, ACTUALLY applying `params` as Tesseract engine parameters.
 *
 * `Tesseract.recognize(image, langs, options)` does NOT take engine parameters:
 * its third argument is forwarded to `createWorker(langs, oem, options)` as
 * WORKER options (corePath, langPath, logger…), and engine parameters live in
 * createWorker's FOURTH argument. So anything like `tessedit_char_whitelist`
 * passed to `recognize` is silently dropped — no error, no warning, just worse
 * recognition. That was a live bug here: `ocr.spec.ts` passes an A-Z whitelist
 * that never reached the engine, and the suite papered over the resulting
 * misreads by regenerating the image until OCR happened to succeed.
 *
 * When there are no parameters this stays on the plain `recognize` path, so the
 * common case is byte-for-byte what it was.
 */
async function recognizeWithParams(
  buffer: Buffer,
  language: string,
  params: Partial<WorkerParams>,
): Promise<RecognizeResult> {
  const Tesseract = await loadTesseract();
  const image = normalizePolarity(buffer);
  if (!Object.keys(params).length) {
    return Tesseract.recognize(image, language);
  }
  const worker = await Tesseract.createWorker(language, undefined, undefined, params);
  try {
    return await worker.recognize(image);
  } finally {
    // Terminating is not optional: a leaked worker keeps a live child process
    // and the node process never exits.
    await worker.terminate();
  }
}

/**
 * Tesseract is trained on DARK text over a LIGHT page and reads light-on-dark
 * material materially worse — which is a problem here, because light-on-dark is
 * the normal case for the thing this module exists to read: game canvases and
 * HUDs. Measured on the `ocr.spec.ts` fixture, white-on-black `BRAVO` came back
 * as `=]=¥:\V/e)` at confidence 0 while every neighbouring word scored 91-97;
 * inverting the same image read it as `BRAVO` at 93 with no other word changed.
 *
 * So a predominantly dark image is inverted before recognition. Geometry is
 * untouched, so every reported bounding box stays in screenshot coordinates.
 *
 * This is deterministic (a mean-luminance threshold, not a heuristic retry) and
 * a no-op on light-background images, which take the byte-identical old path.
 * Non-PNG input is passed through unchanged — `decodePng` is the only decoder
 * here, and failing to sniff the polarity must never fail the recognition.
 */
function normalizePolarity(buffer: Buffer): Buffer {
  let pixels;
  try {
    pixels = decodePng(buffer);
  } catch {
    return buffer;
  }

  const { data } = pixels;
  // Rec. 601 luma, sampled every 16th pixel: a full pass over a 4K screenshot
  // is millions of iterations for a number that only needs to land on the right
  // side of a threshold.
  const stride = 16 * 4;
  let total = 0;
  let samples = 0;
  for (let i = 0; i < data.length; i += stride) {
    total += 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    samples++;
  }
  if (!samples || total / samples >= 128) return buffer;

  // The inverted page goes to Tesseract as a binary PPM — its RGB bytes behind a one-line text header — rather than
  // re-encoded as a PNG: Leptonica reads both, and Tesseract read the same words at the same boxes and confidences from
  // either (compared on ocr.spec.ts's level fixture, 2026-10-06), while a PNG encode is the costliest step of this whole
  // function (a deflate pass in JS over every byte: ~1 s for a 10-megapixel page, ~11 s with coverage instrumenting it).
  // Alpha is not a colour channel and Tesseract reads none, so the page loses nothing it would have used.
  const header = Buffer.from(`P6\n${pixels.width} ${pixels.height}\n255\n`, 'latin1');
  const inverted = Buffer.allocUnsafe(pixels.width * pixels.height * 3);
  for (let i = 0, j = 0; i < data.length; i += 4, j += 3) {
    inverted[j] = 255 - data[i];
    inverted[j + 1] = 255 - data[i + 1];
    inverted[j + 2] = 255 - data[i + 2];
  }
  return Buffer.concat([header, inverted]);
}

export async function recognizeTextOnScreen(source: Page | Locator, options: OcrOptions = {}): Promise<RecognizedText> {
  const language = options.language ?? 'eng';
  const confidenceThreshold = options.confidenceThreshold ?? 60;
  const { buffer, width, height, offsetX, offsetY } = await captureScreenshot(source, options.cropRect);

  const result = await recognizeWithParams(buffer, language, options.tesseractOptions ?? {});

  // Filter low-confidence symbols from the raw data to reduce downstream noise
  const minConfidence = confidenceThreshold;
  const filterSegments = (segments?: TesseractSegment[]): TesseractSegment[] =>
    (segments ?? []).filter((segment: TesseractSegment) => (segment.confidence ?? 0) >= minConfidence);

  result.data.words = filterSegments(result.data.words);
  result.data.lines = filterSegments(result.data.lines);
  result.data.paragraphs = filterSegments(result.data.paragraphs);

  return {
    raw: result,
    screenshot: {
      width,
      height,
      offsetX,
      offsetY,
    },
  };
}

export async function recognizeTextFromImage(image: string | Buffer, options: OcrOptions = {}): Promise<RecognizeResult> {
  const language = options.language ?? 'eng';
  const buffer = await readImageBuffer(image);
  return recognizeWithParams(buffer, language, options.tesseractOptions ?? {});
}

export async function findTextMatches(source: Page | Locator, query: TextQuery, options: OcrSearchOptions = {}): Promise<OcrMatch[]> {
  const matchMode: MatchMode = options.matchMode ?? 'word';
  const matchStrategy: MatchStrategy = options.matchStrategy ?? 'substring';
  const caseSensitive = options.caseSensitive ?? false;
  const confidenceThreshold = options.confidenceThreshold ?? 60;
  const maxResults = options.maxResults ?? Number.POSITIVE_INFINITY;

  const recognition = await recognizeTextOnScreen(source, options);
  const segments = getSegments(recognition.raw, matchMode);

  const matches: OcrMatch[] = [];

  for (const segment of segments) {
    if (matches.length >= maxResults) {
      break;
    }

    const text = segment.text ?? '';
    if (!text.trim()) {
      continue;
    }

    const confidence = segment.confidence ?? 0;
    if (confidence < confidenceThreshold) {
      continue;
    }

    const bbox = segment.bbox ?? segment.box;
    if (!bbox) {
      continue;
    }

    if (!matchesQuery(text, query, matchStrategy, caseSensitive)) {
      continue;
    }

    matches.push({
      text: text.trim(),
      confidence,
      level: matchMode,
      bounds: mapBoundingBox(bbox, recognition.screenshot.offsetX, recognition.screenshot.offsetY),
    });
  }

  return matches;
}

export async function getTextCountOnScreen(source: Page | Locator, query: TextQuery, options: OcrSearchOptions = {}): Promise<number> {
  const matches = await findTextMatches(source, query, options);
  return matches.length;
}

export async function waitForTextOnScreen(
  source: Page | Locator,
  query: TextQuery,
  options: OcrSearchOptions & { maxWait?: number; stepDelay?: number } = {},
): Promise<boolean> {
  const maxWait = options.maxWait ?? 5000;
  const stepDelay = options.stepDelay ?? 500;
  const start = Date.now();

  while (Date.now() - start < maxWait) {
    const matches = await findTextMatches(source, query, options);
    if (matches.length > 0) {
      return true;
    }
    await new Promise((resolve) => setTimeout(resolve, stepDelay));
  }

  return false;
}

export async function clickTextOnScreen(page: Page, query: TextQuery, options: OcrSearchOptions = {}): Promise<void> {
  const matches = await findTextMatches(page, query, options);

  if (matches.length === 0) {
    throw new Error(`Text "${typeof query === 'string' ? query : query.toString()}" not found on screen.`);
  }

  const first = matches[0];
  const clickX = first.bounds.x + first.bounds.width / 2;
  const clickY = first.bounds.y + first.bounds.height / 2;

  await page.mouse.click(clickX, clickY);
}
